/**
 * UTM Tracking Utility — First-Touch Attribution
 * 
 * UTM params are captured ONCE on first landing and never overwritten.
 * This ensures attribution reflects "where did this user originally come from",
 * not later visits or scans.
 */

const UTM_STORAGE_KEY = "pzm_utm_params";

export interface UtmParams {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  gclid: string | null;
  landing_page: string | null;
  landing_timestamp: string | null;
}

/**
 * Capture and persist UTM params from current URL.
 * FIRST-TOUCH ONLY: if UTM params are already stored, do nothing.
 * This prevents later visits (e.g. scanning a business card QR code)
 * from overwriting the original attribution.
 */
export const captureUtmParams = (): void => {
  // If we already have stored UTM params, never overwrite (first-touch rule)
  const existing = localStorage.getItem(UTM_STORAGE_KEY);
  if (existing) {
    try {
      const parsed = JSON.parse(existing) as UtmParams;
      if (parsed.utm_source || parsed.utm_medium || parsed.utm_campaign || parsed.gclid) {
        return; // Already have first-touch attribution, keep it
      }
    } catch {
      // Invalid JSON, allow overwrite
    }
  }

  const params = new URLSearchParams(window.location.search);
  
  const hasUtm = params.has("utm_source") || 
                 params.has("utm_medium") || 
                 params.has("utm_campaign");
  const hasGclid = params.has("gclid");
  
  if (!hasUtm && !hasGclid) return;
  
  const gclid = params.get("gclid");
  
  const utmParams: UtmParams = {
    utm_source: params.get("utm_source") || (hasGclid ? "Google" : null),
    utm_medium: params.get("utm_medium") || (hasGclid ? "cpc" : null),
    utm_campaign: params.get("utm_campaign") || (hasGclid ? "Google Ads (Auto-tagged)" : null),
    utm_content: params.get("utm_content"),
    utm_term: params.get("utm_term"),
    gclid: gclid,
    landing_page: window.location.pathname,
    landing_timestamp: new Date().toISOString(),
  };
  
  localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmParams));
};

/**
 * Get stored first-touch UTM params.
 * Returns stored params only — does NOT fall back to current URL params,
 * because current URL params may represent a later visit, not the original source.
 */
export const getStoredUtmParams = (): UtmParams => {
  const stored = localStorage.getItem(UTM_STORAGE_KEY);
  
  if (stored) {
    try {
      return JSON.parse(stored) as UtmParams;
    } catch {
      // Invalid JSON, return empty
    }
  }
  
  return {
    utm_source: null,
    utm_medium: null,
    utm_campaign: null,
    utm_content: null,
    utm_term: null,
    gclid: null,
    landing_page: null,
    landing_timestamp: null,
  };
};

/**
 * Clear stored UTM params (e.g., after successful conversion/registration)
 */
export const clearUtmParams = (): void => {
  localStorage.removeItem(UTM_STORAGE_KEY);
};

/**
 * Check if first-touch UTM params are present
 */
export const hasUtmParams = (): boolean => {
  const params = getStoredUtmParams();
  return !!(params.utm_source || params.utm_medium || params.utm_campaign);
};
